import { ReactNode, useState, useMemo, useEffect } from 'react';

import createSafeContext from '#/utils/createSafeContext';
import { useFuturesActiveMarketStoreView } from '#/features/perpetuals/active-market-context';
import { useActiveMarketSummaryState } from '#/features/perpetuals/market-info/markets-summary-context';
import { useWalletView } from '#/features/wallet/wallet-context';
import { useAccountView } from '#/features/account/account-context';
import { Market } from '#/api/markets';
import { OrderFormView, createView } from './form-view';
import { OrderFormState, INITIAL_STATE } from './form-state';
import { OrderFormActions, createActions } from './form-actions';

export const [useOrderFormView, ViewCtx] =
  createSafeContext<OrderFormView>('OrderFormView');
export const [useOrderFormActions, ActionsCtx] =
  createSafeContext<OrderFormActions>('OrderFormActions');

interface ProviderProps {
  readonly children: ReactNode;
}

export function OrderFormProvider({ children }: ProviderProps) {
  const { activeMarket } = useFuturesActiveMarketStoreView();

  return (
    // With unique `key` for each market, ensure form is reset when active market changes
    <OrderFormProviderContent key={activeMarket?.symbol}>
      {children}
    </OrderFormProviderContent>
  );
}

function OrderFormProviderContent({ children }: ProviderProps) {
  const { activeMarket } = useFuturesActiveMarketStoreView();
  const activeMarketSummary = useActiveMarketSummaryState();
  const walletView = useWalletView();

  const [state, setState] = useState(INITIAL_STATE);
  const accountView = useAccountView();

  const actions = useMemo(() => createActions(setState), [setState]);
  const view = useMemo(
    () =>
      createView(
        state,
        activeMarket,
        activeMarketSummary,
        walletView.isSignedIn,
        accountView.isBeingLiquidated,
      ),
    [
      state,
      activeMarket,
      activeMarketSummary,
      walletView.isSignedIn,
      accountView.isBeingLiquidated,
    ],
  );

  useDefaultAssetInit(state, actions, activeMarket);

  return (
    <ActionsCtx value={actions}>
      <ViewCtx value={view}>{children}</ViewCtx>
    </ActionsCtx>
  );
}

function useDefaultAssetInit(
  state: OrderFormState,
  actions: OrderFormActions,
  activeMarket: Market | null,
) {
  useEffect(() => {
    if (activeMarket == null) return;
    if (state.asset != null) return;

    actions.updateAsset(activeMarket.base_currency);
  }, [actions, activeMarket, state.asset]);
}
